import React, {useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {Tag} from "primereact/tag";

import {SellerIdentityService} from "../../service/SellerIdentityService";
import {chipSeverity, evidenceText} from "./sellerIdentity";

const sellerIdentityService = new SellerIdentityService();
const SEARCH_DELAY_MS = 300;
const STORE_LINK_HINTS = {trendyol: 'trendyol.com/magaza/…-m-…', hepsiburada: 'hepsiburada.com/magaza/…', n11: 'n11.com/magaza/…',
                          koctas: 'koctas.com.tr/…/s/…'};

const row = {display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', padding: '6px 0',
             borderBottom: '1px solid #f3f4f6'};
const sectionTitle = {fontWeight: 700, margin: '14px 0 6px'};
const hint = {color: '#6b7280'};

// A store page, for checking a seller before deciding.
const StoreLink = ({t, url}) => url
    ? <small style={{display: 'block'}}>
        <a href={url} target="_blank" rel="noopener noreferrer">{t('onboarding.sites.identity.openStore', 'Mağazayı aç')}</a>
    </small>
    : null;

// Editor of one chip: the seller's link to one other marketplace. Every action goes through the
// API and the parent reloads the chips. A seller linked elsewhere moves only after a yes.
const SellerLinkEditor = ({t, seller, chip, onChanged, onError, onClose, toast}) => {
    const [busy, setBusy] = useState(false);
    const [conflict, setConflict] = useState(null);   // {data, retry}
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [storeUrl, setStoreUrl] = useState('');
    const [storeError, setStoreError] = useState('');
    const member = chip.link?.member;
    const candidates = chip.link?.candidates || [];
    const otherNamed = (name) => ({marketplace_key: chip.key, seller_name: name});

    useEffect(() => {
        const wanted = query.trim();
        if (!wanted) {
            setResults([]);
            return undefined;
        }
        let current = true;
        const timer = setTimeout(() => {
            sellerIdentityService.searchSellers(chip.key, wanted)
                .then(found => { if (current) setResults(found || []); })
                .catch(onError);
        }, SEARCH_DELAY_MS);
        return () => { current = false; clearTimeout(timer); };
    }, [query, chip.key]); // eslint-disable-line react-hooks/exhaustive-deps

    // Runs one action. A conflict waits for the person's answer; on a yes it runs again with move.
    const run = (action, onFailure) => {
        setBusy(true);
        setConflict(null);
        return action(false)
            .then(() => onChanged())
            .catch(error => {
                const data = error?.response?.status === 409 ? error.response.data?.data : null;
                if (data?.type === 'stale') {
                    toast.current.show({severity: 'warn', summary: t('onboarding.sites.identity.staleTitle', 'Eşleşmeler değişti'),
                                        detail: t('onboarding.sites.identity.staleDetail', 'Lütfen güncel hâline bakıp tekrar deneyin.')});
                    return onChanged();
                }
                if (data) {
                    setConflict({data, retry: () => run(() => action(true), onFailure)});
                    return undefined;
                }
                if (onFailure && onFailure(error)) return undefined;
                onError(error);
                return undefined;
            })
            .finally(() => setBusy(false));
    };

    const linkTo = (name) => run((move) => sellerIdentityService.link(seller, otherNamed(name), move));
    const rejectOf = (name) => run(() => sellerIdentityService.reject(seller, otherNamed(name)));
    const linkStore = () => {
        setStoreError('');
        return run((move) => sellerIdentityService.storeLink(seller, chip.key, storeUrl.trim(), move).then(() => setStoreUrl('')),
            (error) => {
                const data = error?.response?.status === 400 ? error.response.data : null;
                if (!data?.error_key) return false;
                setStoreError(t(data.error_key, data.error));
                return true;
            });
    };

    const footer = conflict &&
        <div>
            <Button label={t('actions.cancel', 'İptal')} className="p-button-text" disabled={busy} onClick={() => setConflict(null)}/>
            <Button label={t('onboarding.sites.identity.moveHere', 'Buraya taşı')} icon="pi pi-arrow-right" loading={busy}
                    onClick={() => conflict.retry()}/>
        </div>;

    return (
        <Dialog header={`${seller.seller_name} → ${chip.label}`} visible style={{width: '560px', maxWidth: '95vw'}} modal
                footer={footer} onHide={() => !busy && onClose()}>
            {conflict &&
                <Message severity="warn" style={{display: 'block', marginBottom: 10}}
                         text={conflict.data.type === 'other_identity'
                             ? t('onboarding.sites.identity.conflictOther', '{{seller}} şu an "{{group}}" grubunda. Yalnızca bu satıcı buraya taşınır; diğer eşleşmeler kalır.',
                                 {seller: conflict.data.member?.seller_name, group: conflict.data.identity_name})
                             : t('onboarding.sites.identity.conflictTaken', '"{{group}}" grubunda bu pazaryerinde {{seller}} var. Yerini bu satıcı alırsa {{seller}} reddedilmiş sayılır.',
                                 {seller: conflict.data.member?.seller_name, group: conflict.data.identity_name})}/>}

            {member &&
                <div style={row}>
                    <span>
                        <strong>{member.seller_name}</strong>{' '}
                        <Tag severity={chipSeverity({state: member.status})}
                             value={member.status === 'confirmed' ? t('onboarding.sites.identity.confirmed', 'onaylandı')
                                                                  : t('onboarding.sites.identity.automatic', 'otomatik')}/>
                        <br/><small style={hint}>{evidenceText(member.evidence, t)}</small>
                        <StoreLink t={t} url={member.store_url}/>
                    </span>
                    <span style={{display: 'flex', gap: 4, flexShrink: 0}}>
                        {member.status !== 'confirmed' &&
                            <Button label={t('onboarding.sites.identity.confirm', 'Onayla')} className="p-button-sm p-button-success"
                                    disabled={busy} onClick={() => linkTo(member.seller_name)}/>}
                        <Button label={member.status === 'confirmed' ? t('onboarding.sites.identity.remove', 'Kaldır')
                                                                     : t('onboarding.sites.identity.reject', 'Reddet')}
                                className="p-button-sm p-button-text p-button-danger" disabled={busy}
                                onClick={() => rejectOf(member.seller_name)}/>
                    </span>
                </div>}

            {candidates.length > 0 &&
                <div>
                    <div style={sectionTitle}>{t('onboarding.sites.identity.candidates', 'Öneriler')}</div>
                    {candidates.map(candidate =>
                        <div key={candidate.id || candidate.seller_name} style={row}>
                            <span>
                                {candidate.seller_name}
                                <br/><small style={hint}>{evidenceText(candidate.evidence, t)}</small>
                                <StoreLink t={t} url={candidate.store_url}/>
                            </span>
                            <span style={{display: 'flex', gap: 4, flexShrink: 0}}>
                                <Button label={t('onboarding.sites.identity.confirm', 'Onayla')} className="p-button-sm p-button-success"
                                        disabled={busy} onClick={() => linkTo(candidate.seller_name)}/>
                                <Button label={t('onboarding.sites.identity.reject', 'Reddet')} className="p-button-sm p-button-text p-button-danger"
                                        disabled={busy} onClick={() => rejectOf(candidate.seller_name)}/>
                            </span>
                        </div>)}
                </div>}

            <div style={sectionTitle}>{t('onboarding.sites.identity.searchTitle', 'Bu pazaryerinde ara')}</div>
            <InputText id="sellerSearchInput" value={query} style={{width: '100%'}}
                       placeholder={t('onboarding.sites.sellerSearch', 'Satıcı ara')}
                       onChange={(e) => setQuery(e.target.value)}/>
            {results.map(found =>
                <div key={found.seller_name} style={row}>
                    <span>
                        {found.seller_name}
                        {found.identity_id &&
                            <small style={{...hint, display: 'block'}}>
                                {t('onboarding.sites.identity.inGroup', '"{{group}}" grubunda', {group: found.identity_name})}
                            </small>}
                        <StoreLink t={t} url={found.store_url}/>
                    </span>
                    <Button label={t('onboarding.sites.identity.pick', 'Eşleştir')} className="p-button-sm p-button-outlined"
                            disabled={busy} onClick={() => linkTo(found.seller_name)}/>
                </div>)}

            <div style={sectionTitle}>{t('onboarding.sites.identity.storeLinkTitle', 'Mağaza linki yapıştır')}</div>
            <div style={{display: 'flex', gap: 8}}>
                <InputText id="sellerStoreLink" value={storeUrl} style={{flex: 1}} className={storeError ? 'p-invalid' : ''}
                           placeholder={STORE_LINK_HINTS[chip.key] || ''}
                           onChange={(e) => { setStoreUrl(e.target.value); setStoreError(''); }}/>
                <Button label={t('onboarding.sites.identity.linkStore', 'Bağla')} disabled={busy || !storeUrl.trim()} onClick={linkStore}/>
            </div>
            {storeError && <small style={{color: '#e24c4c', display: 'block', marginTop: 4}}>{storeError}</small>}
        </Dialog>
    );
};

export default SellerLinkEditor;
